import { motion } from "framer-motion";
import { ArrowRight, Award, Calendar, Users } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import "./HomePage.css";
import PaymentModal from "./PaymentModel";

const HomePage = () => {
  const navigate = useNavigate();
  const API_BASE_URL = process.env.REACT_APP_API_ENTRYPOINT;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const hasVerified = useRef(false);

  const [modal, setModal] = useState({
    visible: false,
    message: "",
    type: "success",
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const orderId = params.get("order_id");

    if (orderId && !hasVerified.current) {
      hasVerified.current = true;

      (async () => {
        try {
          const data = await api.payment.verify(orderId);
          console.log("✅ Verify Response:", data);

          const spCode = data?.verifyResponse?.sp_code || "N/A";
          const spMessage = data?.verifyResponse?.sp_message || "Unknown";

          if (spCode === "1000") {
            setModal({
              visible: true,
              message: `Registration done successfully!!`,
              type: "success",
            });
          } else if (spCode === "1001") {
            setModal({
              visible: true,
              message: `Registration failed!`,
              type: "error",
            });
          } else if (spCode === "1002") {
            setModal({
              visible: true,
              message: `Registration failed!`,
              type: "warning",
            });
          } else {
            setModal({
              visible: true,
              message: `Payment verification failed.`,
              type: "error",
            });
          }
        } catch (error) {
          console.error(error);
          setModal({
            visible: true,
            message: "Failed to verify payment. (Network or server error)",
            type: "warning",
          });
        }
      })();
    }
  }, [API_BASE_URL]);

  const handleRegisterClick = () => {
    navigate("/register");
    setIsMobileMenuOpen(false);
  };

  const handleDirectoryClick = () => {
    navigate("/directory");
    setIsMobileMenuOpen(false);
  };

  
  const [hpMsg, setHpMsg] = useState([]);

  useEffect(() => {
    const fetchTShirtSizes = async () => {
      try {
        const data = await api.lookup.getTShirtByGender("HomeMessage");
       
        setHpMsg(data);
      } catch (err) {
        console.error("Error fetching t-shirt sizes:", err);
      }
    };

    fetchTShirtSizes();
  }, []);

  const features = [
    {
      icon: Calendar,
      title: "KGC'97 Day 2026",
      description: "Join us for an unforgettable get together",
    },
    {
      icon: Users,
      title: "Connect & Network",
      description: "Meet Friends and Family",
    },
    {
      icon: Award,
      title: "Special Events",
      description: "Exciting activities and competitions",
    },
  ];

  const [message, setMessage] = useState("");
  const [registrationActive, setRegistrationActive] = useState(null);
  const { user } = useAuth();
  const [myRegistrations, setMyRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasFetchedRegistrations, setHasFetchedRegistrations] = useState(false);
  const [mobileNumber, setMobileNumber] = useState("");

  // Combined search states
  const [directoryResults, setDirectoryResults] = useState([]);
  const [participantResults, setParticipantResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [activeTab, setActiveTab] = useState("all"); // 'all', 'directory', 'participant'
  const [afterSearch, setAfterSearch] = useState(false);

  useEffect(() => {
    const fetchMessage = async () => {
      try {
        const data = await api.lookup.getMessage();
        if (data && data.length > 0) {
          setMessage(data[0].nameNative);
        }
      } catch (err) {
        console.error("Error fetching message:", err);
      }
    };

    fetchMessage();
  }, []);

  useEffect(() => {
    const fetchRegistrationStatus = async () => {
      try {
        const data = await api.lookup.getRegistrationStatus();
        setRegistrationActive(data);
      } catch (err) {
        console.error("Error fetching registration status:", err);
        setRegistrationActive(false);
      }
    };

    fetchRegistrationStatus();
  }, []);

  // Combined search function
  const handleCombinedSearch = async () => {
    if (!mobileNumber || mobileNumber.length < 11) {
      setSearchError("Please enter a valid 11-digit mobile number");
      return;
    }

    try {
      setSearchLoading(true);
      setSearchError("");

      // Clear previous results
      setDirectoryResults([]);
      setParticipantResults([]);

      // Search in directories (new API endpoint)
      try {
        const dirData = await api.directory.searchByMobile(mobileNumber);
        setDirectoryResults(dirData || []);
      } catch (dirErr) {
        console.error("Error fetching directory results:", dirErr);
        setDirectoryResults([]);
      }

      // Search in participants (existing API)
      try {
        const partData = await api.registration.getMyRegistrations(
          mobileNumber
        );
        setParticipantResults(partData || []);
      } catch (partErr) {
        console.error("Error fetching participant results:", partErr);
        setParticipantResults([]);
      }

      setActiveTab("all");
      setAfterSearch(true);
    } catch (error) {
      console.error("Search error:", error);
      setSearchError("Failed to search. Please try again.");
    } finally {
      setSearchLoading(false);
    }
  };

  const handleResetCombinedSearch = () => {
    setMobileNumber("");
    setDirectoryResults([]);
    setParticipantResults([]);
    setSearchError("");
    setActiveTab("all");
    setHasFetchedRegistrations(false);
    setMyRegistrations([]);
  };

  const handleMobileNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 11) {
      setMobileNumber(value);
      setSearchError("");
    }
  };

  const handleEditRegistration = (registrationId) => {
    navigate(`/edit-registration/${registrationId}`);
  };

  const handleViewDetails = (registrationId) => {
    navigate(`/registration-details/${registrationId}`);
  };

  const isPaymentCompleted = (paymentStatus) => {
    return paymentStatus === "Success" || paymentStatus === "Paid";
  };

  return (
    <div className="homepage-container">
      {/* Hero Section */}
      <div className="hero-section">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="hero-content"
        >
          {/* Top Left Logo */}
          <div className="logo-container">
            <img
              src={process.env.PUBLIC_URL + "/images/home_logo.jpeg"}
              alt="SZMC Logo"
              className="logo-img"
            />
          </div>

          {/* Title */}
          <div className="hero-text">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="hero-title"
            >
              KGC'97 DAY 2026
            </motion.h1>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="hero-subtitle"
            >
              HSC Batch 1997
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="hero-organization"
            >
              KGC'97 Welfare Association
            </motion.p>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="hero-college"
            >
              কুষ্টিয়া সরকারি কলেজ, কুষ্টিয়া
            </motion.p>
          </div>

          {/* Desktop Buttons */}
          <div className="d-none d-md-flex justify-content-center gap-3 mt-4">
            <div className="button-wrapper">
              <button
                className="directory-btn d-flex align-items-center justify-content-center"
                onClick={handleDirectoryClick}
              >
                Directory Update
                <ArrowRight className="ms-2" size={18} />
              </button>
            </div>
            <div className="button-wrapper">
              {registrationActive ? (
                <button
                  className="register-btn d-flex align-items-center justify-content-center"
                  onClick={handleRegisterClick}
                >
                  Regisration
                  <ArrowRight className="ms-2" size={18} />
                </button>
              ) : (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="registration-closed-text"
                >
                  🛑 Registration Closed
                </motion.p>
              )}
            </div>
          </div>

          {/* Mobile Buttons */}
          <div className="d-md-none mt-4">
            <div className="row g-2 align-items-stretch">
              <div className="col-6 d-flex">
                <button
                  className="directory-btn w-100 py-2 d-flex align-items-center justify-content-center"
                  onClick={handleDirectoryClick}
                >
                  Directory Update
                </button>
              </div>
              <div className="col-6 d-flex">
                {registrationActive ? (
                  <button
                    className="register-btn w-100 py-2 d-flex align-items-center justify-content-center"
                    onClick={handleRegisterClick}
                  >
                    Registration
                  </button>
                ) : (
                  <div className="btn btn-secondary w-100 py-3 disabled d-flex align-items-center justify-content-center">
                    Registration Closed
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* <div className="d-md-none mt-4">
            <div className="row g-2">
              <div className="col-6">
                <button
                  className="directory-btn w-100 py-3"
                  onClick={handleDirectoryClick}
                >
                  Directory Update
                </button>
              </div>
              <div className="col-6">
                {registrationActive ? (
                  <button
                    className="register-btn w-100 py-3"
                    onClick={handleRegisterClick}
                  >
                    Registration
                  </button>
                ) : (
                  <div className="btn btn-secondary w-100 py-3 disabled">
                    Registration Closed
                  </div>
                )}
              </div>
            </div>
          </div> */}
        </motion.div>

        {/* Message Section */}
        <div className="hero-text mt-4 mt-md-12">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            <div className="solid-bg px-4 pt-4 rounded-2xl shadow-xl h-100">
              <div className="text-center text-white text-sm md:text-base">
                {/* Directory Update & Event Registration Instructions */}
                <div className="space-y-4">
                  {/* Directory Information Section */}
                  <div className="mb-4">
                    <h3 className="font-bold text-lg mb-2 text-green-200">
                      ডিরেক্টরি আপডেট ও ইভেন্ট রেজিস্ট্রেশন নির্দেশনা
                    </h3>

                    <div className="text-left space-y-3">
                      {/* Directory Info */}
                      <div>
                        <p className="font-semibold text-amber-300 mb-1">
                          <span className="text-xl">Direcotry </span> (ব্যক্তিগত
                          তথ্য):
                        </p>
                        <p className="ml-2">
                          নাম, মোবাইল নম্বর, গ্রুপ, ইমেইল, ঠিকানা ইত্যাদি
                          সঠিকভাবে পূরণ করো। এই তথ্যগুলো রিইউনিয়ন ডিরেক্টরিতে
                          প্রকাশিত হবে।
                          <span className="block text-gray-300 mt-1 text-sm">
                            (প্রদত্ত তথ্য ভবিষ্যৎ যোগাযোগ ও স্মরণিকার জন্য
                            ব্যবহৃত হবে)
                          </span>
                        </p>
                      </div>

                      {/* Event Registration */}
                      <div>
                        <p className="font-semibold text-amber-300 mb-1 text-xl">
                          Event Registration:
                        </p>
                        <p className="ml-2">
                          রিইউনিয়নে অংশগ্রহণের জন্য নির্ধারিত ফি ও
                          অংশগ্রহণকারীর সংখ্যা (Self / Family / Guest) নির্বাচন
                          করো।
                        </p>
                      </div>

                      {/* Verification */}
                      <div>
                        <p className="font-semibold text-amber-300 mb-1">
                          <span className="text-xl">Verification</span> (তথ্য
                          যাচাই):
                        </p>
                        <p className="ml-2">
                          সব তথ্য সঠিকভাবে পূরণ হয়েছে কিনা নিশ্চিত হয়ে ফর্ম
                          সাবমিট করো।
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.p>
        </div>
        <div className="hero-text mt-0 mt-md-12">
  <motion.p
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ delay: 0.7 }}
  >
    <div className="solid-bg px-4 pt-2 rounded-2xl shadow-xl h-100">
      <div className="text-center text-white text-sm md:text-base">
        {/* Dynamically render contact information from API */}
        {hpMsg.length > 0 ? (
          // Group items by keyword to ensure message and contact are paired
          Object.entries(
            hpMsg.reduce((acc, item) => {
              if (!acc[item.keyword]) {
                acc[item.keyword] = {};
              }
              // Store message and contact based on nameNative
              if (item.nameNative === "msg") {
                acc[item.keyword].message = item.name;
              } else if (item.nameNative === "contact") {
                acc[item.keyword].contact = item.name;
              }
              return acc;
            }, {})
          )
            .sort(([keyA], [keyB]) => keyA.localeCompare(keyB)) // Sort by keyword
            .map(([keyword, data]) => (
              <p 
                key={keyword} 
                className={keyword !== "1" ? "mt-2" : ""} // Add margin-top except for first item
              >
                {data.message}
                <br className="md:hidden" />
                <span className="block md:inline-block mt-1 md:mt-0 md:ml-1">
                  {data.contact}
                </span>
              </p>
            ))
        ) : (
          // Fallback content if no data is available
          <>
            <p>
              ডিরেক্টরি আপডেট সংক্রান্ত সমস্যায় যোগাযোগ:
              <br className="md:hidden" />
              <span className="block md:inline-block mt-1 md:mt-0 md:ml-1">
                01712-823877 (মাসুদ)
              </span>
            </p>
            <p className="mt-2">
              রেজিস্ট্রেশন ও পেমেন্ট সংক্রান্ত সমস্যায় যোগাযোগ:
              <br className="md:hidden" />
              <span className="block md:inline-block mt-1 md:mt-0 md:ml-1">
                01711-065316 (শিশির)...
              </span>
            </p>
          </>
        )}
      </div>
    </div>
  </motion.p>
</div>

        {/* Reg update Message Section */}
        <div className="hero-text mt-0 mt-md-12">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="message-text"
          >
            {message || " "}
          </motion.p>
        </div>

        {/* Combined Search Section */}
        {/* <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="mt-8 mt-md-12 w-100 px-3 px-md-4"
        >
          <div className="solid-bg rounded-2xl shadow-xl p-4 p-md-5 w-100">
            <div className="text-center mb-4">
              <h2 className="text-lg text-md-2xl fw-bold text-green-100 d-flex align-items-center justify-content-center gap-2">
                <Search className="text-green-300" size={24} />
                Search by Mobile Number
              </h2>
            </div>

            <div className="row justify-content-center">
              <div className="col-12 col-md-8 col-lg-6">
                <div className="input-group input-group-lg">
                  <input
                    type="text"
                    className="form-control bg-light text-dark border-green-500"
                    placeholder="Enter mobile number"
                    value={mobileNumber}
                    onChange={handleMobileNumberChange}
                    maxLength={11}
                    style={{ height: "50px" }}
                  />
                  {directoryResults.length > 0 ||
                  participantResults.length > 0 ? (
                    <button
                      className="btn btn-secondary"
                      onClick={handleResetCombinedSearch}
                      disabled={searchLoading}
                      style={{ height: "50px" }}
                    >
                      Reset
                    </button>
                  ) : (
                    <button
                      className="btn btn-primary d-flex align-items-center gap-2"
                      onClick={handleCombinedSearch}
                      disabled={searchLoading || !mobileNumber.trim()}
                      style={{ height: "50px" }}
                    >
                      {searchLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm" />
                          Searching...
                        </>
                      ) : (
                        <>
                          <Search size={20} />
                          Search
                        </>
                      )}
                    </button>
                  )}
                </div>
                {searchError && (
                  <div className="text-danger small mt-2 text-center">
                    {searchError}
                  </div>
                )}
              </div>
            </div>

            {afterSearch && (
              <div className="mt-4">
                <div className="text-center mb-3">
                  <div className="badge bg-info text-dark me-2">
                    Directory: {directoryResults.length} found
                  </div>
                  <div className="badge bg-warning text-dark">
                    Participants: {participantResults.length} found
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div> */}

        {/* Combined Search Results Section */}
        {/* {(directoryResults.length > 0 || participantResults.length > 0) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="mt-4 w-100 px-3 px-md-4"
          >
            <div className="solid-bg rounded-2xl shadow-xl p-3 p-md-4 w-100">
              
              {(activeTab === "all" || activeTab === "directory") &&
                directoryResults.length > 0 && (
                  <div className="mb-5">
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <Users className="text-primary" size={20} />
                      <h3 className="fw-bold text-white mb-0">
                        Directory Entries
                      </h3>
                      <span className="badge bg-primary">
                        {directoryResults.length}
                      </span>
                    </div>

                    <div className="d-md-none">
                      {directoryResults.map((item) => {
                        // Check if this directory entry has a corresponding participant registration
                        const hasParticipant = participantResults.some(
                          (participant) => participant.mobile === item.mobile
                        );

                        return (
                          <div
                            key={`dir-mobile-${item.id}`}
                            className="card bg-dark-50 border-0 shadow-sm mb-3"
                          >
                            <div className="card-body">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <div>
                                  <h6 className="fw-bold mb-1">{item.name}</h6>
                                  <small className="text-muted">
                                    {item.mobile}
                                  </small>
                                </div>
                                <div className="d-flex flex-column align-items-end">
                                  <span className="badge bg-danger mb-1">
                                    {item.bloodGroup || "N/A"}
                                  </span>
                                  {!hasParticipant && (
                                    <span className="badge bg-warning text-dark">
                                      Not Registered
                                    </span>
                                  )}
                                </div>
                              </div>

                              <div className="row g-2 mb-3">
                                <div className="col-6">
                                  <small className="text-muted d-block">
                                    Group
                                  </small>
                                  <span className="badge bg-primary">
                                    {item.batch}
                                  </span>
                                </div>
                                <div className="col-6">
                                  <small className="text-muted d-block">
                                    Profession
                                  </small>
                                  <div>{item.profession || "N/A"}</div>
                                </div>
                                <div className="col-12">
                                  <small className="text-muted d-block">
                                    Email
                                  </small>
                                  <div>{item.email || "N/A"}</div>
                                </div>
                                <div className="col-12">
                                  <small className="text-muted d-block">
                                    Address
                                  </small>
                                  <div className="small">
                                    {item.permanentAddress ||
                                      item.officeAddress ||
                                      "N/A"}
                                  </div>
                                </div>
                              </div>

                              {!hasParticipant && (
                                <div className="d-grid gap-2">
                                  <button
                                    onClick={() =>
                                      navigate(`/edit-directory/${item.id}`)
                                    }
                                    className="btn btn-warning btn-sm"
                                  >
                                    Edit Directory Entry
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="d-none d-md-block">
                      <div className="table-responsive">
                        <table className="table table-light table-hover mb-0">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Group</th>
                              <th>Profession</th>
                              <th>Email</th>
                              <th>Blood Group</th>
                              <th>Address</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {directoryResults.map((item) => {
                              // Check if this directory entry has a corresponding participant registration
                              const hasParticipant = participantResults.some(
                                (participant) =>
                                  participant.mobile === item.mobile
                              );

                              return (
                                <tr key={`dir-${item.id}`}>
                                  <td>
                                    <div className="fw-bold">{item.name}</div>
                                    <div className="small text-muted">
                                      {item.mobile}
                                    </div>
                                  </td>
                                  <td>{item.batch}</td>
                                  <td>{item.profession || "N/A"}</td>
                                  <td>{item.email || "N/A"}</td>
                                  <td>
                                    <span className="badge bg-danger">
                                      {item.bloodGroup || "N/A"}
                                    </span>
                                  </td>
                                  <td className="small">
                                    {item.permanentAddress ? (
                                      <div>{item.permanentAddress}</div>
                                    ) : (
                                      <div>{item.officeAddress || "N/A"}</div>
                                    )}
                                  </td>
                                  <td>
                                    {hasParticipant ? (
                                      <span className="badge bg-success">
                                        Registered
                                      </span>
                                    ) : (
                                      <span className="badge bg-warning">
                                        Not Registered
                                      </span>
                                    )}
                                  </td>
                                  <td>
                                    {!hasParticipant && (
                                      <div className="d-flex flex-column gap-2">
                                        <button
                                          onClick={() =>
                                            navigate(
                                              `/edit-directory/${item.id}`
                                            )
                                          }
                                          className="btn btn-secondary btn-sm"
                                        >
                                          Edit
                                        </button>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

              {(activeTab === "all" || activeTab === "participant") &&
                participantResults.length > 0 && (
                  <div>
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <User className="text-success" size={20} />
                      <h3 className="fw-bold text-white mb-0">
                        Participant Registrations
                      </h3>
                      <span className="badge bg-success">
                        {participantResults.length}
                      </span>
                    </div>

                    <div className="d-md-none">
                      {participantResults.map((p) => (
                        <div
                          key={`part-mobile-${p.id}`}
                          className="card bg-light-50 border-0 shadow-sm mb-3"
                        >
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div>
                                <h6 className="fw-bold mb-1">{p.name}</h6>
                                <small className="text-muted">{p.email}</small>
                              </div>
                              <span
                                className={`badge ${
                                  p.paymentStatus === "Success" ||
                                  p.paymentStatus === "Paid"
                                    ? "bg-success"
                                    : "bg-warning"
                                }`}
                              >
                                {p.paymentStatus === "Success" ||
                                p.paymentStatus === "Paid"
                                  ? "Paid"
                                  : p.paymentStatus || "Pending"}
                              </span>
                            </div>

                            <div className="row g-2 mb-3">
                              <div className="col-6">
                                <small className="text-muted d-block">
                                  Batch
                                </small>
                                <span className="badge bg-primary">
                                  {p.batch}
                                </span>
                              </div>
                              <div className="col-6">
                                <small className="text-muted d-block">
                                  Mobile
                                </small>
                                <div>{p.mobile}</div>
                              </div>
                              <div className="col-6">
                                <small className="text-muted d-block">
                                  Participant T-Shirt
                                </small>
                                <div className="fw-bold">
                                  {p.tshirtSize || "N/A"}
                                </div>
                              </div>
                              <div className="col-6">
                                <small className="text-muted d-block">
                                  Adult T-Shirt
                                </small>
                                <div className="fw-bold">
                                  {p.adultTshirtSize || "N/A"}
                                </div>
                              </div>
                              <div className="col-6">
                                <small className="text-muted d-block">
                                  Child T-Shirt
                                </small>
                                <div className="fw-bold">
                                  {p.childBelow5TshirtSize || "N/A"}
                                </div>
                              </div>
                              <div className="col-6">
                                <small className="text-muted d-block">
                                  Amount
                                </small>
                                <div className="fw-bold">
                                  ৳
                                  {p.totalAmount
                                    ? p.totalAmount.toLocaleString()
                                    : "0"}
                                </div>
                              </div>
                              <div className="col-6">
                                <small className="text-muted d-block">
                                  Adults
                                </small>
                                <div>{p.accompany?.adult || 0}</div>
                              </div>
                              <div className="col-6">
                                <small className="text-muted d-block">
                                  Children
                                </small>
                                <div>{p.accompany?.childBelow5 || 0}</div>
                              </div>
                            </div>

                            <div className="d-grid gap-2">
                              {!isPaymentCompleted(p.paymentStatus) && (
                                <button
                                  onClick={() => handleEditRegistration(p.id)}
                                  className="btn btn-primary btn-sm"
                                >
                                  Edit Registration
                                </button>
                              )}
                              <button
                                onClick={() =>
                                  navigate(`/registration-details/${p.id}`)
                                }
                                className="btn btn-outline-light btn-sm"
                              >
                                View Details
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="d-none d-md-block">
                      <div className="table-responsive">
                        <table className="table table-light table-hover mb-0">
                          <thead>
                            <tr>
                              <th>Participant Info</th>
                              <th>Contact</th>
                              <th>Payment Info</th>
                              <th>T-Shirt Sizes</th>
                              <th>Accompany Info</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {participantResults.map((p) => (
                              <tr key={`part-${p.id}`}>
                                <td style={{ minWidth: "130px" }}>
                                  <div className="fw-bold">{p.name}</div>
                                  <div>
                                    <span className="badge bg-primary">
                                      {p.batch}
                                    </span>
                                  </div>
                                </td>

                                <td style={{ minWidth: "120px" }}>
                                  <div>{p.mobile}</div>
                                  <div className="small text-muted">
                                    {p.email}
                                  </div>
                                </td>

                                <td style={{ minWidth: "130px" }}>
                                  <div className="small">
                                    <div>
                                      <span
                                        className={`badge ${
                                          p.paymentStatus === "Success" ||
                                          p.paymentStatus === "Paid"
                                            ? "bg-success"
                                            : "bg-warning"
                                        }`}
                                      >
                                        {p.paymentStatus === "Success" ||
                                        p.paymentStatus === "Paid"
                                          ? "Paid"
                                          : p.paymentStatus || "Pending"}
                                      </span>
                                    </div>
                                    <div>
                                      <strong>Amount:</strong>{" "}
                                      {p.totalAmount
                                        ? `৳${p.totalAmount.toLocaleString()}`
                                        : "N/A"}
                                    </div>
                                    <div>
                                      <strong>Date:</strong>{" "}
                                      {new Date(
                                        p.registrationDate
                                      ).toLocaleDateString()}
                                    </div>
                                  </div>
                                </td>

                                <td style={{ minWidth: "150px" }}>
                                  <div className="small">
                                    <div>
                                      <strong>Participant:</strong>{" "}
                                      {p.tshirtSize || "N/A"}
                                    </div>
                                    <div>
                                      <strong>Adult:</strong>{" "}
                                      {p.adultTshirtSize || "N/A"}
                                    </div>
                                    <div>
                                      <strong>Child:</strong>{" "}
                                      {p.childBelow5TshirtSize || "N/A"}
                                    </div>
                                  </div>
                                </td>

                                <td style={{ minWidth: "150px" }}>
                                  <div className="small">
                                    <div>
                                      <strong>Adults:</strong>{" "}
                                      {p.accompany?.adult || 0}
                                    </div>
                                    <div>
                                      <strong>Children:</strong>{" "}
                                      {p.accompany?.childBelow5 || 0}
                                    </div>
                                  </div>
                                </td>

                                <td style={{ minWidth: "100px" }}>
                                  <div className="d-flex flex-column gap-2">
                                    {!isPaymentCompleted(p.paymentStatus) && (
                                      <button
                                        onClick={() =>
                                          handleEditRegistration(p.id)
                                        }
                                        className="btn btn-primary btn-sm"
                                      >
                                        Edit
                                      </button>
                                    )}
                                    <button
                                      onClick={() =>
                                        navigate(
                                          `/registration-details/${p.id}`
                                        )
                                      }
                                      className="btn btn-secondary btn-sm"
                                    >
                                      View
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
            </div>
          </motion.div>
        )} */}

        {/* Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1 }}
          className="mt-0 mt-md-12 w-100 px-3 px-md-0"
        >
          <div className="row g-3 g-md-4 justify-content-center">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2 + index * 0.2 }}
                whileHover={{ scale: 1.05, y: -5 }}
                className="col-12 col-md-6 col-lg-4"
              >
                <div className="solid-bg p-4 rounded-2xl shadow-xl h-100">
                  <div className="bg-gradient-to-br from-green-500 to-green-200 w-12 h-12 w-md-16 h-md-16 rounded-xl d-flex align-items-center justify-content-center mb-3 mx-auto">
                    <feature.icon className="text-white" size={24} />
                  </div>
                  <h3 className="text-lg text-md-xl fw-bold mb-2 text-center text-white">
                    {feature.title}
                  </h3>
                  <p className="text-green-200 text-center small text-md-base">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Event Image */}
        {/* <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.5 }}
          className="mt-8 mt-md-12 w-100 px-3 px-md-0"
        >
          <div className="image-card rounded-2xl overflow-hidden">
            <img
              src="https://horizons-cdn.hostinger.com/c93cb817-a80c-4da5-9d48-ef791d571152/b3bdcc49043c6218254c38361afa3887.jpg"
              alt="KGC College"
              className="event-image w-100 h-auto"
            />
          </div>
        </motion.div> */}
      </div>

      {/* Footer */}
      <footer className="mt-0 mt-md-12 py-0 text-center text-green-500">
        <div className="container">
          <p className="mb-2">
            © {new Date().getFullYear()} KGC'97 Welfare Association
          </p>
          <p className="small">All rights reserved</p>
        </div>
      </footer>

      {modal.visible && (
        <PaymentModal
          {...modal}
          onClose={() => setModal({ ...modal, visible: false })}
        />
      )}
    </div>
  );
};

export default HomePage;

// import { motion } from "framer-motion";
// import {
//   ArrowRight,
//   Award,
//   Calendar,
//   Sparkles,
//   User,
//   Users,
//   Search,
// } from "lucide-react";
// import React, { useCallback, useEffect, useRef, useState } from "react";
// import { useNavigate } from "react-router-dom";
// import { useAuth } from "../context/AuthContext";
// import api from "../services/api";
// import "./HomePage.css";
// import PaymentModal from "./PaymentModel";

// const HomePage = () => {
//   const navigate = useNavigate();
//   const API_BASE_URL = process.env.REACT_APP_API_ENTRYPOINT;
//   const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
//   const hasVerified = useRef(false);

//   const [modal, setModal] = useState({
//     visible: false,
//     message: "",
//     type: "success",
//   });

//   useEffect(() => {
//     const params = new URLSearchParams(window.location.search);
//     const orderId = params.get("order_id");

//     if (orderId && !hasVerified.current) {
//       hasVerified.current = true;

//       (async () => {
//         try {
//           const data = await api.payment.verify(orderId);
//           console.log("✅ Verify Response:", data);

//           const spCode = data?.verifyResponse?.sp_code || "N/A";
//           const spMessage = data?.verifyResponse?.sp_message || "Unknown";

//           if (spCode === "1000") {
//             setModal({
//               visible: true,
//               message: `Registration done successfully!!`,
//               type: "success",
//             });
//           } else if (spCode === "1001") {
//             setModal({
//               visible: true,
//               message: `Registration failed!`,
//               type: "error",
//             });
//           } else if (spCode === "1002") {
//             setModal({
//               visible: true,
//               message: `Registration failed!`,
//               type: "warning",
//             });
//           } else {
//             setModal({
//               visible: true,
//               message: `Payment verification failed.`,
//               type: "error",
//             });
//           }
//         } catch (error) {
//           console.error(error);
//           setModal({
//             visible: true,
//             message: "Failed to verify payment. (Network or server error)",
//             type: "warning",
//           });
//         }
//       })();
//     }
//   }, [API_BASE_URL]);

//   const handleRegisterClick = () => {
//     navigate("/register");
//     setIsMobileMenuOpen(false);
//   };

//   const handleDirectoryClick = () => {
//     navigate("/directory");
//     setIsMobileMenuOpen(false);
//   };

//   const features = [
//     {
//       icon: Calendar,
//       title: "KGC'97 Day 2026",
//       description: "Join us for an unforgettable celebration",
//     },
//     {
//       icon: Users,
//       title: "Connect & Network",
//       description: "Meet alumni and fellow students",
//     },
//     {
//       icon: Award,
//       title: "Special Events",
//       description: "Exciting activities and competitions",
//     },
//   ];

//   const [message, setMessage] = useState("");
//   const [registrationActive, setRegistrationActive] = useState(null);
//   const { user } = useAuth();
//   const [myRegistrations, setMyRegistrations] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [hasFetchedRegistrations, setHasFetchedRegistrations] = useState(false);
//   const [mobileNumber, setMobileNumber] = useState("");
//   const [mobileSearchMode, setMobileSearchMode] = useState(false);
//   const [mobileSearchLoading, setMobileSearchLoading] = useState(false);
//   const [mobileError, setMobileError] = useState("");

//   useEffect(() => {
//     const fetchMessage = async () => {
//       try {
//         const data = await api.lookup.getMessage();
//         if (data && data.length > 0) {
//           setMessage(data[0].nameNative);
//         }
//       } catch (err) {
//         console.error("Error fetching message:", err);
//       }
//     };

//     fetchMessage();
//   }, []);

//   useEffect(() => {
//     const fetchRegistrationStatus = async () => {
//       try {
//         const data = await api.lookup.getRegistrationStatus();
//         setRegistrationActive(data);
//       } catch (err) {
//         console.error("Error fetching registration status:", err);
//         setRegistrationActive(false);
//       }
//     };

//     fetchRegistrationStatus();
//   }, []);

//   const fetchRegistrationsByMobile = async (mobile) => {
//     if (!mobile || mobile.length < 11) {
//       setMobileError("Please enter a valid 11-digit mobile number");
//       return;
//     }

//     try {
//       setMobileSearchLoading(true);
//       setMobileError("");
//       // Pass mobile number to the API call
//       const data = await api.registration.getMyRegistrations(mobile);
//       setMyRegistrations(data || []);
//       setMobileSearchMode(true);
//     } catch (error) {
//       console.error("❌ Error fetching registrations by mobile:", error);
//       setMobileError("Failed to fetch registrations. Please try again.");
//       setMyRegistrations([]);
//     } finally {
//       setMobileSearchLoading(false);
//     }
//   };

//   const handleMobileSearch = () => {
//     if (mobileNumber.trim()) {
//       fetchRegistrationsByMobile(mobileNumber);
//     }
//   };

//   const handleResetSearch = () => {
//     setMobileNumber("");
//     setMobileSearchMode(false);
//     setMobileError("");
//     setHasFetchedRegistrations(false);

//       setMyRegistrations([]);

//   };

//   const handleMobileNumberChange = (e) => {
//     const value = e.target.value.replace(/\D/g, "");
//     if (value.length <= 11) {
//       setMobileNumber(value);
//       setMobileError("");
//     }
//   };

//   const handleEditRegistration = (registrationId) => {
//     navigate(`/edit-registration/${registrationId}`);
//   };

//   const handleViewDetails = (registrationId) => {
//     navigate(`/registration-details/${registrationId}`);
//   };

//   const isPaymentCompleted = (paymentStatus) => {
//     return paymentStatus === "Success" || paymentStatus === "Paid";
//   };

//   return (
//     <div className="homepage-container">
//       {/* Animated Background */}
//       {/* <div className="animated-background">
//         <motion.div
//           className="green-circle"
//           animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
//           transition={{ duration: 8, repeat: Infinity }}
//         />
//         <motion.div
//           className="green-circle"
//           animate={{ scale: [1.2, 1, 1.2], opacity: [0.5, 0.3, 0.5] }}
//           transition={{ duration: 10, repeat: Infinity }}
//         />
//       </div> */}

//       {/* Hero Section */}
//       <div className="hero-section">
//         <motion.div
//           initial={{ opacity: 0, y: 30 }}
//           animate={{ opacity: 1, y: 0 }}
//           transition={{ duration: 0.8 }}
//           className="hero-content"
//         >
//           {/* Top Left Logo */}
//           <div className="logo-container">
//   <img
//     src={process.env.PUBLIC_URL + "/images/home_logo.jpeg"}
//     alt="SZMC Logo"
//     className="logo-img"
//   />
// </div>
//           {/* Sparkles Icon */}
//           {/* <motion.div
//             initial={{ scale: 0 }}
//             animate={{ scale: 1 }}
//             transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
//             className="sparkles-wrapper"
//           >
//             <div className="sparkles-bg" />
//             <div className="sparkles-icon-container">
//               <Sparkles className="sparkles-icon" />
//             </div>
//           </motion.div> */}

//           {/* Title */}
//           <div className="hero-text">
//             <motion.h1
//               initial={{ opacity: 0, y: 20 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ delay: 0.3 }}
//               className="hero-title"
//             >
//               KGC'97 DAY 2026
//             </motion.h1>
//             <motion.h2
//               initial={{ opacity: 0, y: 20 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ delay: 0.4 }}
//               className="hero-subtitle"
//             >
//               HSC Batch 1997
//             </motion.h2>
//             <motion.p
//               initial={{ opacity: 0 }}
//               animate={{ opacity: 1 }}
//               transition={{ delay: 0.5 }}
//               className="hero-organization"
//             >
//               KGC Welfare Association
//             </motion.p>
//             <motion.p
//               initial={{ opacity: 0 }}
//               animate={{ opacity: 1 }}
//               transition={{ delay: 0.6 }}
//               className="hero-college"
//             >
//               কুষ্টিয়া সরকারি কলেজ, কুষ্টিয়া
//             </motion.p>
//           </div>

//           {/* Desktop Buttons */}
//           <div className="d-none d-md-flex justify-content-center gap-3 mt-4">
//             <div className="button-wrapper">
//               <button
//                 className="directory-btn d-flex align-items-center justify-content-center"
//                 onClick={handleDirectoryClick}
//               >
//                 Directory
//                 <ArrowRight className="ms-2" size={18} />
//               </button>
//             </div>
//             <div className="button-wrapper">
//               {registrationActive ? (
//                 <button
//                   className="register-btn d-flex align-items-center justify-content-center"
//                   onClick={handleRegisterClick}
//                 >
//                   Register
//                   <ArrowRight className="ms-2" size={18} />
//                 </button>
//               ) : (
//                 <motion.p
//                   initial={{ opacity: 0 }}
//                   animate={{ opacity: 1 }}
//                   transition={{ delay: 0.3 }}
//                   className="registration-closed-text"
//                 >
//                   🛑 Registration Closed
//                 </motion.p>
//               )}
//             </div>
//           </div>

//           {/* Mobile Buttons */}
//           <div className="d-md-none mt-4">
//             <div className="row g-2">
//               <div className="col-6">
//                 <button
//                   className="directory-btn w-100 py-3"
//                   onClick={handleDirectoryClick}
//                 >
//                   Directory
//                 </button>
//               </div>
//               <div className="col-6">
//                 {registrationActive ? (
//                   <button
//                     className="register-btn w-100 py-3"
//                     onClick={handleRegisterClick}
//                   >
//                     Register
//                   </button>
//                 ) : (
//                   <div className="btn btn-secondary w-100 py-3 disabled">
//                     Registration Closed
//                   </div>
//                 )}
//               </div>
//             </div>
//           </div>
//         </motion.div>

//         {/* Message Section */}
//         <div className="hero-text mt-5 mt-md-12">
//           <motion.p
//             initial={{ opacity: 0 }}
//             animate={{ opacity: 1 }}
//             transition={{ delay: 0.7 }}
//             className="message-text"
//           >
//             {message || " "}
//           </motion.p>
//         </div>

//          {/* Mobile Search Section - BEFORE Features Section */}
//          <motion.div
//           initial={{ opacity: 0, y: 20 }}
//           animate={{ opacity: 1, y: 0 }}
//           transition={{ delay: 0.9 }}
//           className="mt-8 mt-md-12 w-100 px-3 px-md-4"
//         >
//           {/* <div className="glass-effect rounded-2xl shadow-xl p-4 p-md-5 w-100"> */}
//           <div className="solid-bg rounded-2xl shadow-xl p-4 p-md-5 w-100">

//             <div className="text-center mb-4">
//             <h2 className="text-lg text-md-2xl fw-bold text-green-100 d-flex align-items-center justify-content-center gap-2">
//               {/* <h2 className="text-lg text-md-2xl fw-bold text-green-300 d-flex align-items-center justify-content-center gap-2"> */}
//                 {/* <Search className="text-green-300" size={24} /> */}
//                 Search Participant by Mobile
//               </h2>
//               {/* <p className="text-gray-300 mb-0">
//                 Enter a mobile number to check registration status
//               </p> */}
//             </div>

//             {/* Mobile Search Input and Button */}
//             <div className="row justify-content-center">
//               <div className="col-12 col-md-8 col-lg-6">
//                 <div className="input-group input-group-lg">
//                   <input
//                     type="text"
//                     className="form-control bg-light text-dark border-green-500"
//                     placeholder="Enter mobile number"
//                     value={mobileNumber}
//                     onChange={handleMobileNumberChange}
//                     maxLength={11}
//                     style={{ height: "50px" }}
//                   />
//                   {mobileSearchMode ? (
//                     <button
//                       className="btn btn-secondary"
//                       onClick={handleResetSearch}
//                       disabled={mobileSearchLoading}
//                       style={{ height: "50px" }}
//                     >
//                       Reset
//                     </button>
//                   ) : (
//                     <button
//                       className="btn btn-primary d-flex align-items-center gap-2"
//                       onClick={handleMobileSearch}
//                       disabled={mobileSearchLoading || !mobileNumber.trim()}
//                       style={{ height: "50px" }}
//                     >
//                       {mobileSearchLoading ? (
//                         <>
//                           <span className="spinner-border spinner-border-sm" />
//                           Searching...
//                         </>
//                       ) : (
//                         <>
//                           <Search size={20} />
//                           Search
//                         </>
//                       )}
//                     </button>
//                   )}
//                 </div>
//                 {mobileError && (
//                   <div className="text-danger small mt-2 text-center">
//                     {mobileError}
//                   </div>
//                 )}
//                 {mobileSearchMode && (
//                   <div className="text-info small mt-2 text-center">
//                     Showing results for mobile: {mobileNumber}
//                   </div>
//                 )}
//               </div>
//             </div>
//           </div>
//         </motion.div>

//         {/* My Registrations Section */}
//         {(user || mobileSearchMode) && (
//           <motion.div
//             initial={{ opacity: 0, y: 20 }}
//             animate={{ opacity: 1, y: 0 }}
//             transition={{ delay: 0.8 }}
//             className="mt-8 mt-md-12 w-100 px-3 px-md-4"
//           >
//             <div className="solid-bg rounded-2xl shadow-xl p-3 p-md-6 w-100">
//               <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-4">
//                 <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-3 mb-3 mb-md-0">
//                   <h2 className="text-lg text-md-2xl fw-bold text-green-300 d-flex align-items-center gap-2">
//                     <User className="text-green-300" size={20} />
//                     {mobileSearchMode ? "Search Results" : "My Registrations"}
//                   </h2>
//                   {mobileSearchMode && (
//                     <div className="badge bg-warning text-dark">
//                       Searching by: {mobileNumber}
//                     </div>
//                   )}
//                 </div>
//                 <div className="text-sm text-gray-300">
//                   {myRegistrations.length} registration
//                   {myRegistrations.length !== 1 ? "s" : ""} found
//                 </div>
//               </div>

//               {/* Mobile Cards View */}
//               <div className="d-md-none">
//                 {mobileSearchLoading || loading ? (
//                   <div className="text-center py-4">
//                     <div className="spinner-border text-primary" role="status">
//                       <span className="visually-hidden">Loading...</span>
//                     </div>
//                   </div>
//                 ) : myRegistrations.length === 0 ? (
//                   <div className="text-center py-4 text-gray-400">
//                     {mobileSearchMode
//                       ? `No registrations found for mobile: ${mobileNumber}`
//                       : "No registrations found"}
//                   </div>
//                 ) : (
//                   <div className="row g-3">
//                     {myRegistrations.map((p) => (
//                       <div key={p.id} className="col-12">
//                         <div className="card bg-dark-50 border-0 shadow-sm mb-3">
//                           <div className="card-body">
//                             <div className="d-flex justify-content-between align-items-start mb-2">
//                               <div>
//                                 <h6 className="fw-bold mb-1">{p.name}</h6>
//                                 <small className="text-muted">{p.email}</small>
//                               </div>
//                               <span
//                                 className={`badge ${
//                                   p.paymentStatus === "Success" ||
//                                   p.paymentStatus === "Paid"
//                                     ? "bg-success"
//                                     : "bg-warning"
//                                 }`}
//                               >
//                                 {p.paymentStatus === "Success" ||
//                                 p.paymentStatus === "Paid"
//                                   ? "Paid"
//                                   : p.paymentStatus || "Pending"}
//                               </span>
//                             </div>

//                             <div className="row g-2 mb-3">
//                               <div className="col-6">
//                                 <small className="text-muted d-block">
//                                   Batch
//                                 </small>
//                                 <span className="badge bg-primary">
//                                   {p.batch}
//                                 </span>
//                               </div>
//                               <div className="col-6">
//                                 <small className="text-muted d-block">
//                                   Mobile
//                                 </small>
//                                 <div>{p.mobile}</div>
//                               </div>
//                               <div className="col-6">
//                                 <small className="text-muted d-block">
//                                   Participant T-Shirt
//                                 </small>
//                                 <div className="fw-bold">
//                                   {p.tshirtSize || "N/A"}
//                                 </div>
//                               </div>
//                               <div className="col-6">
//                                 <small className="text-muted d-block">
//                                   Adult T-Shirt
//                                 </small>
//                                 <div className="fw-bold">
//                                   {p.adultTshirtSize || "N/A"}
//                                 </div>
//                               </div>
//                               <div className="col-6">
//                                 <small className="text-muted d-block">
//                                   Child T-Shirt
//                                 </small>
//                                 <div className="fw-bold">
//                                   {p.childBelow5TshirtSize || "N/A"}
//                                 </div>
//                               </div>
//                               <div className="col-6">
//                                 <small className="text-muted d-block">
//                                   Amount
//                                 </small>
//                                 <div className="fw-bold">
//                                   ৳
//                                   {p.totalAmount
//                                     ? p.totalAmount.toLocaleString()
//                                     : "0"}
//                                 </div>
//                               </div>
//                             </div>

//                             <div className="d-grid gap-2">
//                             {!isPaymentCompleted(p.paymentStatus) && (
//                               <button
//                                 onClick={() => handleEditRegistration(p.id)}
//                                 className="btn btn-primary btn-sm"
//                               >
//                                 Edit Registration
//                               </button>
//                             )}
//                               <button
//                                 onClick={() =>
//                                   navigate(`/registration-details/${p.id}`)
//                                 }
//                                 className="btn btn-outline-light btn-sm"
//                               >
//                                 View Details
//                               </button>
//                             </div>
//                           </div>
//                         </div>
//                       </div>
//                     ))}
//                   </div>
//                 )}
//               </div>

//               {/* Desktop Table View */}
//               <div className="d-none d-md-block">
//                 <div className="card border-0 shadow-sm">
//                   <div className="card-body" style={{ overflowX: "auto" }}>
//                     <div className="table-responsive">
//                       <table className="table table-hover mb-0">
//                         <thead className="table-bright">
//                           <tr>
//                             <th>Participant Info</th>
//                             <th>Contact Info</th>
//                             <th>Payment Info</th>
//                             <th>Transaction Info</th>
//                             <th>T-Shirt Sizes</th>
//                             <th>Accompany Info</th>
//                             <th>Actions</th>
//                           </tr>
//                         </thead>
//                         <tbody>
//                           {myRegistrations.map((p) => (
//                             <tr key={p.id}>
//                               <td style={{ minWidth: "130px" }}>
//                                 <div className="fw-bold">{p.name}</div>
//                                 <div>
//                                   <span className="badge bg-primary">
//                                     {p.batch}
//                                   </span>
//                                 </div>
//                               </td>

//                               <td style={{ minWidth: "120px" }}>
//                                 <div>{p.mobile}</div>
//                                 <div className="small text-muted">
//                                   {p.email}
//                                 </div>
//                               </td>

//                               <td style={{ minWidth: "130px" }}>
//                                 <div className="small">
//                                   <div>
//                                     <span
//                                       className={`badge ${
//                                         p.paymentStatus === "Success" ||
//                                         p.paymentStatus === "Paid"
//                                           ? "bg-success"
//                                           : "bg-warning"
//                                       }`}
//                                     >
//                                       {p.paymentStatus === "Success" ||
//                                       p.paymentStatus === "Paid"
//                                         ? "Paid"
//                                         : p.paymentStatus || "Pending"}
//                                     </span>
//                                   </div>
//                                   <div>
//                                     <strong>Amount:</strong>{" "}
//                                     {p.totalAmount
//                                       ? `৳${p.totalAmount.toLocaleString()}`
//                                       : "N/A"}
//                                   </div>
//                                   <div>
//                                     <strong>Method:</strong>{" "}
//                                     {p.paymentMethod || "N/A"}
//                                   </div>
//                                   <div>
//                                     <strong>Date:</strong>{" "}
//                                     {new Date(
//                                       p.registrationDate
//                                     ).toLocaleDateString()}
//                                   </div>
//                                 </div>
//                               </td>

//                               <td style={{ minWidth: "200px" }}>
//                                 <div className="small">
//                                   <div>
//                                     <strong>Order:</strong> {p.orderId || "N/A"}
//                                   </div>
//                                   <div>
//                                     <strong>Txn:</strong>{" "}
//                                     {p.transactionId || "N/A"}
//                                   </div>
//                                   <div>
//                                     <strong>Bank:</strong>{" "}
//                                     {p.bankTransactionId || "N/A"}
//                                   </div>
//                                 </div>
//                               </td>

//                               {/* T-Shirt Sizes Column */}
//                               <td style={{ minWidth: "150px" }}>
//                                 <div className="small">
//                                   <div>
//                                     <strong>Participant:</strong>{" "}
//                                     {p.tshirtSize || "N/A"}
//                                   </div>
//                                   <div>
//                                     <strong>Adult:</strong>{" "}
//                                     {p.adultTshirtSize || "N/A"}
//                                   </div>
//                                   <div>
//                                     <strong>Child:</strong>{" "}
//                                     {p.childBelow5TshirtSize || "N/A"}
//                                   </div>
//                                 </div>
//                               </td>

//                               {/* Accompany Info Column */}
//                               <td style={{ minWidth: "150px" }}>
//                                 <div className="small">
//                                   <div>
//                                     <strong>Adults:</strong>{" "}
//                                     {p.accompany?.adult || 0}
//                                   </div>
//                                   <div>
//                                     <strong>Children:</strong>{" "}
//                                     {p.accompany?.childBelow5 || 0}
//                                   </div>
//                                 </div>
//                               </td>
//                               <td style={{ minWidth: "100px" }}>
//                                 <div className="d-flex flex-column gap-2">
//                                 {!isPaymentCompleted(p.paymentStatus) && (
//                                   <button
//                                     onClick={() => handleEditRegistration(p.id)}
//                                     className="btn btn-primary btn-sm"
//                                   >
//                                     Edit
//                                   </button>
//                                 )}
//                                   <button
//                                     onClick={() =>
//                                       navigate(`/registration-details/${p.id}`)
//                                     }
//                                     className="btn btn-secondary btn-sm"
//                                   >
//                                     View
//                                   </button>
//                                 </div>
//                               </td>
//                             </tr>
//                           ))}
//                         </tbody>
//                       </table>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </motion.div>
//         )}

//         {/* Features Grid */}
//         <motion.div
//           initial={{ opacity: 0, y: 50 }}
//           animate={{ opacity: 1, y: 0 }}
//           transition={{ delay: 1.1 }}
//           className="mt-8 mt-md-12 w-100 px-3 px-md-0"
//         >
//           <div className="row g-3 g-md-4 justify-content-center">
//             {features.map((feature, index) => (
//               <motion.div
//                 key={index}
//                 initial={{ opacity: 0, y: 20 }}
//                 animate={{ opacity: 1, y: 0 }}
//                 transition={{ delay: 1.2 + index * 0.2 }}
//                 whileHover={{ scale: 1.05, y: -5 }}
//                 className="col-12 col-md-6 col-lg-4"
//               >
//                 <div className="solid-bg p-4 rounded-2xl shadow-xl h-100">
//                   <div className="bg-gradient-to-br from-green-500 to-green-200 w-12 h-12 w-md-16 h-md-16 rounded-xl d-flex align-items-center justify-content-center mb-3 mx-auto">
//                     <feature.icon className="text-white" size={24} />
//                   </div>
//                   <h3 className="text-lg text-md-xl fw-bold mb-2 text-center text-white">
//                     {feature.title}
//                   </h3>
//                   <p className="text-green-200 text-center small text-md-base">
//                     {feature.description}
//                   </p>
//                 </div>
//               </motion.div>
//             ))}
//           </div>
//         </motion.div>

//         {/* Event Image */}
//         <motion.div
//           initial={{ opacity: 0, scale: 0.9 }}
//           animate={{ opacity: 1, scale: 1 }}
//           transition={{ delay: 1.5 }}
//           className="mt-8 mt-md-12 w-100 px-3 px-md-0"
//         >
//           <div className="image-card rounded-2xl overflow-hidden">
//             {/* <img
//               src="https://horizons-cdn.hostinger.com/c93cb817-a80c-4da5-9d48-ef791d571152/b3bdcc49043c6218254c38361afa3887.jpg"
//               alt="KGC College"
//               className="event-image w-100 h-auto"
//             /> */}
//           </div>
//         </motion.div>
//       </div>

//       {/* Footer */}
//       <footer className="mt-8 mt-md-12 py-4 text-center text-gray-400">
//         <div className="container">
//           <p className="mb-2">
//             © {new Date().getFullYear()} KGC'97 Welfare Association
//           </p>
//           <p className="small">All rights reserved</p>
//         </div>
//       </footer>

//       {modal.visible && (
//         <PaymentModal
//           {...modal}
//           onClose={() => setModal({ ...modal, visible: false })}
//         />
//       )}
//     </div>
//   );
// };

// export default HomePage;
